import {
  APIAttachment,
  APIComponentAttributes,
  APIComponentType,
  APIFieldAttributes,
  APIModelAttributes,
  APIReferenceAttributes,
  APIReferenceType,
  APIScenarioScopeData,
  APIScopeData,
  APIContributor,
  APITagAttributes,
  APIWorkspaceAttributes,
  ArdoqId,
  APIScopeDataWithExplicitParentChildReferences,
  APIParentChildReferenceAttributes,
  APIOrganizationUser,
  APIEntityType,
  MetaData,
  Versioned,
} from '@ardoq/api-types';
import { VIRTUAL_MODEL_ID } from '@ardoq/global-consts';
import {
  deepFreeze,
  mergeArrays,
  isTruthy,
  toByIdDictionary,
} from '@ardoq/common-helpers';
import { modelOperations, referenceAttributesOperations } from '@ardoq/core';
import {
  EnhancedScopeData,
  VirtualReference,
  WorkspaceAndModelLookup,
} from '@ardoq/data-model';
import { partition, uniq, uniqBy } from 'lodash';
import { generateHierarchy } from './internal/componentHierarchy';
import { generateGraphData } from './internal/generateGraphData';
import {
  toModelTypeDictionary,
  toModelTypes,
} from './internal/modelTypesUtils';

const property =
  <T, K extends keyof T>(key: K) =>
  (obj: T): T[K] =>
    obj[key];

export type EnhanceScopeDataConfig = {
  readonly shouldFreeze?: boolean;
  /**
   * When provided, `graphData`'s reference adjacency (sourceMap/targetMap/
   * referenceMap) is limited to these reference ids. This mirrors the
   * visible-reference filtering `graphModel$` applies in viewpoint mode.
   * `references`/`referencesById` are left complete. When omitted, `graphData`
   * covers all references in the scope data.
   */
  readonly graphReferenceIds?: readonly ArdoqId[];
  /**
   * Virtual (collapsed-path) references to fold into the scope data. They are
   * appended to `references` with `model: VIRTUAL_MODEL_ID`, and a synthetic
   * model carrying their reference types is added to `models` so the regular
   * type/style/colour lookups resolve them. Replaces the backbone
   * `virtualModel` registration for the enhanced-scope-data path.
   */
  readonly virtualReferences?: readonly VirtualReference[];
  /**
   * Audit metadata stamped onto the synthesized virtual references (built once
   * by the caller from the current user). Defaults to empty values so the
   * synthesized references are always complete `APIReferenceAttributes`.
   */
  readonly virtualReferenceMetadata?: MetaData & Versioned;
};

// Empty audit metadata, used when the caller doesn't supply any, so a
// synthesized virtual reference is always a complete `APIReferenceAttributes`.
const EMPTY_VIRTUAL_REFERENCE_METADATA: MetaData & Versioned = {
  _version: 0,
  created: '',
  createdBy: '',
  createdByEmail: '',
  createdByName: '',
  lastModifiedBy: '',
  lastModifiedByEmail: '',
  lastModifiedByName: '',
  lastUpdated: '',
};

const toVirtualReferenceAttributes = (
  reference: VirtualReference,
  metadata: MetaData & Versioned
): APIReferenceAttributes => ({
  ...metadata,
  createdProvenance: null,
  lastProvenance: null,
  _id: reference.referenceId,
  // Virtual reference type ids are synthetic strings, whereas
  // `APIReferenceAttributes.type` is typed `number`. This single field is cast;
  // the scope-data lookups key by `String(type)`, so the string round-trips.
  type: reference.referenceTypeId as unknown as number,
  displayText: reference.name,
  source: reference.sourceId,
  target: reference.targetId,
  model: VIRTUAL_MODEL_ID,
  rootWorkspace: reference.rootWorkspace,
  targetWorkspace: reference.targetWorkspace,
  order: 0,
  description: '',
  // Mark it as a reference so the graph utils (e.g. isReference, used by
  // isGraphModelValid) treat the collapsed-path edge as a valid reference.
  ardoq: { 'entity-type': APIEntityType.REFERENCE },
});

// Synthetic model holding the virtual reference types, keyed by the string
// reference type id so the enhanced lookups (`referenceTypes[String(type)]`)
// resolve. Only `referenceTypes`/`root` are read by `getModelTypes`; the
// remaining `APIModelAttributes` fields are irrelevant for virtual references.
const buildVirtualModel = (
  virtualReferences: readonly VirtualReference[]
): APIModelAttributes => {
  const referenceTypes: Record<string, APIReferenceType> = {};
  virtualReferences.forEach(({ referenceTypeId, name, referenceStyle }) => {
    referenceTypes[String(referenceTypeId)] = {
      ...referenceStyle,
      id: referenceTypeId,
      name,
      returnsValue: false,
    } as unknown as APIReferenceType;
  });
  return {
    _id: VIRTUAL_MODEL_ID,
    name: 'Virtual',
    referenceTypes,
    root: {},
  } as unknown as APIModelAttributes;
};

const getEmpty = (): APIScopeData => {
  return {
    attachments: [],
    components: [],
    fields: [],
    models: [],
    references: [],
    scopeComponents: [],
    tags: [],
    workspaces: [],
  };
};

const getEmptyScenarioScopeData = (): APIScenarioScopeData => {
  return {
    ...getEmpty(),
    connected: {
      components: [],
      references: [],
      models: [],
      tags: [],
      workspaces: [],
      fields: [],
      connectedComponents: [],
    },
  };
};

const isScenarioScopeData = (
  scopeData: APIScopeData | APIScenarioScopeData
): scopeData is APIScenarioScopeData => {
  return 'connected' in scopeData;
};

/**
 * scopeData.connected is a special case of scopeData that only applies to
 * scenarios and is itself a consistent scopeData collection.
 * Due to type issues, we can't use the same merge function for both.
 */
const mergeConnected = (
  ...scopeDataList: Array<APIScopeData | APIScenarioScopeData>
): APIScenarioScopeData['connected'] => {
  const mergedComponents: APIComponentAttributes[] = [];
  const mergedConnectedComponents: ArdoqId[] = [];
  const mergedFields: APIFieldAttributes[] = [];
  const mergedModels: APIModelAttributes[] = [];
  const mergedReferences: APIReferenceAttributes[] = [];
  const mergedTags: APITagAttributes[] = [];
  const mergedWorkspaces: APIWorkspaceAttributes[] = [];

  scopeDataList.forEach(scopeData => {
    if (!isScenarioScopeData(scopeData)) return;
    const { connected } = scopeData;

    mergedComponents.push(...connected.components);
    mergedConnectedComponents.push(...connected.connectedComponents);
    mergedFields.push(...connected.fields);
    mergedModels.push(...connected.models);
    mergedReferences.push(...connected.references);
    mergedTags.push(...connected.tags);
    mergedWorkspaces.push(...connected.workspaces);
  });

  return {
    components: uniqBy(mergedComponents, '_id'),
    connectedComponents: uniq(mergedConnectedComponents),
    fields: uniqBy(mergedFields, '_id'),
    models: uniqBy(mergedModels, '_id'),
    references: uniqBy(mergedReferences, '_id'),
    tags: uniqBy(mergedTags, '_id'),
    workspaces: uniqBy(mergedWorkspaces, '_id'),
  };
};

/**
 * Merges several scope data values into one. Entities are concatenated in
 * argument order and deduplicated by `_id` keeping the FIRST occurrence — so on
 * overlapping ids the EARLIER argument wins. Pass the more authoritative /
 * fresher scope data first.
 */
function merge(...scopeDataList: Array<APIScopeData>): APIScopeData;
// prettier-ignore
function merge(...scopeDataList: Array<APIScopeData | APIScenarioScopeData>): APIScenarioScopeData;
// prettier-ignore
function merge(...scopeDataList: Array<APIScenarioScopeData>): APIScenarioScopeData;
function merge(...scopeDataList: APIScopeData[]): APIScopeData {
  const mergedAttachments: APIAttachment[] = [];
  const mergedComponents = mergeArrays(
    scopeDataList.map(property('components'))
  );
  const mergedFields: APIFieldAttributes[] = [];
  const mergedModels: APIModelAttributes[] = [];
  const mergedReferences = mergeArrays(
    scopeDataList.map(property('references'))
  );
  const mergedScopeComponents: ArdoqId[] = [];
  const mergedTags: APITagAttributes[] = [];
  const mergedUsers: APIContributor[] = [];
  const mergedOrganizationUsers: APIOrganizationUser[] = [];
  const mergedWorkspaces: APIWorkspaceAttributes[] = [];

  scopeDataList.forEach(scopeData => {
    mergedFields.push(...scopeData.fields);
    mergedModels.push(...scopeData.models);
    mergedScopeComponents.push(...scopeData.scopeComponents);
    mergedTags.push(...scopeData.tags);
    mergedWorkspaces.push(...scopeData.workspaces);
    if (scopeData.attachments) {
      mergedAttachments.push(...scopeData.attachments);
    }
    if (scopeData.users) {
      mergedUsers.push(...scopeData.users);
    }
    if (scopeData.organizationUsers) {
      mergedOrganizationUsers.push(...scopeData.organizationUsers);
    }
  });

  const scopeData: APIScopeData = {
    components: uniqBy(mergedComponents, '_id'),
    fields: uniqBy(mergedFields, '_id'),
    models: uniqBy(mergedModels, '_id'),
    references: uniqBy(mergedReferences, '_id'),
    scopeComponents: uniq(mergedScopeComponents),
    tags: uniqBy(mergedTags, '_id'),
    workspaces: uniqBy(mergedWorkspaces, '_id'),
    attachments: uniqBy(mergedAttachments, '_id'),
    users: uniqBy(mergedUsers, '_id'),
    organizationUsers: uniqBy(mergedOrganizationUsers, '_id'),
  };

  const hasScenarioScopeData = scopeDataList.some(isScenarioScopeData);
  if (!hasScenarioScopeData) {
    return scopeData;
  }

  const scenarioScopeData: APIScenarioScopeData = {
    connected: mergeConnected(...scopeDataList),
    ...scopeData,
  };

  return scenarioScopeData;
}

const enhance = (
  scopeData: APIScopeData,
  config?: EnhanceScopeDataConfig
): EnhancedScopeData => {
  const {
    components,
    tags,
    workspaces,
    attachments,
    fields,
    dateRangeFieldMap,
    scopeComponents,
    users,
    organizationUsers,
  } = scopeData;

  // Fold virtual (collapsed-path) references and their synthetic model into the
  // scope data so the regular lookups resolve them.
  const virtualReferences = config?.virtualReferences ?? [];
  const virtualReferenceMetadata =
    config?.virtualReferenceMetadata ?? EMPTY_VIRTUAL_REFERENCE_METADATA;
  const references = virtualReferences.length
    ? [
        ...scopeData.references,
        ...virtualReferences.map(reference =>
          toVirtualReferenceAttributes(reference, virtualReferenceMetadata)
        ),
      ]
    : scopeData.references;
  const models = virtualReferences.length
    ? [...scopeData.models, buildVirtualModel(virtualReferences)]
    : scopeData.models;

  const typesByModelId = toModelTypes(models);
  const fieldsByModelIdAndTypeId = toModelTypeDictionary(
    fields,
    typesByModelId
  );

  const workspacesById = toByIdDictionary(workspaces);
  const modelsById = toByIdDictionary(models);

  const graphReferenceIdSet = config?.graphReferenceIds
    ? new Set(config.graphReferenceIds)
    : null;
  const graphData = generateGraphData({
    references: graphReferenceIdSet
      ? references.filter(reference => graphReferenceIdSet.has(reference._id))
      : references,
    typesByModelId,
  });

  // The field properties `calculatedFieldSettings`, `defaultValue`,
  // `description`, `label`, `name` and `type`  are meant to be global,
  // e.g. these properties are synced on the backend for all fields with
  // the same name.
  const fieldLabelByFieldName = Object.fromEntries(
    fields.map(({ name, label }) => [name, label])
  );

  const calculatedFields = new Set(
    fields
      .filter(({ calculatedFieldSettings }) => Boolean(calculatedFieldSettings))
      .map(({ name }) => name)
  );

  type WorkspaceMap = Record<ArdoqId, APIWorkspaceAttributes | undefined>;
  const workspacesByModelId: WorkspaceMap = Object.fromEntries(
    workspaces.map(workspace => [workspace.componentModel, workspace])
  );

  const data = {
    components,
    references,
    models,
    tags,
    workspaces,
    fields,
    dateRangeFieldMap: dateRangeFieldMap || new Map(),
    componentsById: toByIdDictionary(components),
    referencesById: toByIdDictionary(references),
    fieldsById: toByIdDictionary(fields),
    workspacesById,
    workspacesByModelId,
    modelsById,
    tagsById: toByIdDictionary(tags),
    typesByModelId,
    fieldsByModelIdAndTypeId,
    graphData,
    hierarchy: generateHierarchy(components),
    fieldLabelByFieldName,
    calculatedFields,
    scopeComponentIds: new Set(scopeComponents),
    users: users ?? [],
    organizationUsers: organizationUsers ?? [],
    attachments: attachments ?? [],
  };

  const shouldFreeze = config?.shouldFreeze !== false;
  return shouldFreeze ? deepFreeze(data) : data;
};

/**
 * Some endpoints (traversals) return explicit parent/child references which
 * is not how ardoq-front handles them.
 * This function removes the parent/child references from scopeData
 */
const partitionParentChildReferences = (
  inputScopeData: APIScopeDataWithExplicitParentChildReferences
): {
  scopeData: APIScopeData;
  parentChildReferences: APIParentChildReferenceAttributes[];
} => {
  const [parentChildReferences, references] = partition(
    inputScopeData.references,
    referenceAttributesOperations.isSyntheticParentChildReference
  );

  const scopeData = { ...inputScopeData, references };
  return { scopeData, parentChildReferences };
};

export const getAncestorBreadcrumbs = (
  component: Pick<APIComponentAttributes, '_id' | 'name' | 'parent'>,
  componentsById: Record<
    string,
    Pick<APIComponentAttributes, '_id' | 'name' | 'parent'>
  >
): string[] => {
  const collect = (
    acc: string[],
    current: Pick<APIComponentAttributes, '_id' | 'name' | 'parent'>,
    visited: Set<string>
  ): string[] => {
    if (!current.parent) return acc;
    if (visited.has(current.parent)) return acc;
    const parent = componentsById[current.parent];
    if (!parent) return acc;
    visited.add(parent._id);
    return collect([...acc, parent.name], parent, visited);
  };
  return collect([], component, new Set([component._id])).reverse();
};

const containsScopeComponent = (
  scopeData: APIScopeData,
  componentId?: ArdoqId | null
): boolean =>
  componentId ? scopeData.scopeComponents.includes(componentId) : false;

export type WorkspaceAndModel = {
  workspace: APIWorkspaceAttributes;
  model: APIModelAttributes;
};

const getWorkspace = (
  lookup: WorkspaceAndModelLookup,
  workspaceId: ArdoqId
): APIWorkspaceAttributes | null => {
  return lookup.workspacesById[workspaceId] ?? null;
};

const getWorkspaceAndModel = (
  lookup: WorkspaceAndModelLookup,
  workspaceId: ArdoqId
): WorkspaceAndModel | null => {
  const workspace = lookup.workspacesById[workspaceId];
  if (!workspace) return null;

  const model = lookup.modelsById[workspace.componentModel];
  if (!model) return null;

  return { workspace, model };
};

const getComponentTypeFromWorkspaceLookup = (
  lookup: WorkspaceAndModelLookup,
  component: APIComponentAttributes
): APIComponentType | null => {
  const workspaceAndModel = getWorkspaceAndModel(
    lookup,
    component.rootWorkspace
  );
  if (!workspaceAndModel) return null;
  return (
    modelOperations.getComponentTypes(workspaceAndModel.model)
      .componentTypesById[component.typeId] ?? null
  );
};

export type WorkspaceWithTypes = {
  workspace: APIWorkspaceAttributes;
  componentTypes: APIComponentType[];
  referenceTypes: APIReferenceType[];
};

const toWorkspaceWithTypes = ({
  workspace,
  model,
}: WorkspaceAndModel): WorkspaceWithTypes => {
  const modelTypes = modelOperations.getModelTypes(model);
  return {
    workspace,
    componentTypes: Object.values(modelTypes.componentTypesById),
    referenceTypes: Object.values(modelTypes.referenceTypesById),
  };
};

const toWorkspacesWithTypes = (
  workspaceAndModel: WorkspaceAndModelLookup,
  workspaceIds: ArdoqId[]
): WorkspaceWithTypes[] =>
  workspaceIds
    .map(id => getWorkspaceAndModel(workspaceAndModel, id))
    .filter(isTruthy)
    .map(toWorkspaceWithTypes);

const getWorkspaceIdsFromComponents = ({
  components,
}: APIScopeData): ArdoqId[] =>
  uniq(components.map(component => component.rootWorkspace));

export const scopeDataOperations = {
  enhance,
  getEmpty,
  getEmptyScenarioScopeData,
  isScenarioScopeData,
  merge,
  partitionParentChildReferences,
  getAncestorBreadcrumbs,
  containsScopeComponent,
  getWorkspace,
  getWorkspaceAndModel,
  getComponentTypeFromWorkspaceLookup,
  toWorkspaceWithTypes,
  toWorkspacesWithTypes,
  getWorkspaceIdsFromComponents,
};
